// The Domain Pricing list: a card with a search box, a registry filter and a pricing filter, then
// a table of the TLDs on sale, in the order resellers see them — the TLD, its Pricing dialog, the
// registry that registers it automatically, and whether resellers see it. A TLD is never removed,
// only withdrawn, so nothing resellers set for it is lost. Superadmin and finance change all of it
// and move each one; anyone else reads (staffGuard is the gate). The search and the filters are the route's
// search params (guide, rule 16): this draws them and hands up what changed; arrangeTlds applies
// them. The route cuts the page and hands in the caption and the pagination.
import type { Registry, TldPricing } from "@kilat/contracts/tld-pricing";
import type { ReactNode } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { m } from "@/lib/paraglide/messages.js";
import { MoveTldButtons } from "./move-tld/buttons";
import { registryItems } from "./registries";
import { SetTldCostDialog } from "./set-tld-cost/dialog";
import { TldOfferedSwitch } from "./set-tld-offered/switch";
import { TldRegistrySelect } from "./set-tld-registry/select";

export const PRICED_FILTERS = ["all", "priced", "unpriced"] as const;
export type PricedFilter = (typeof PRICED_FILTERS)[number];

export const STATUS_FILTERS = ["all", "offered", "withdrawn"] as const;
export type StatusFilter = (typeof STATUS_FILTERS)[number];

export type TldSearch = { q: string; registry: Registry | "all"; status: StatusFilter; priced: PricedFilter };

/** What the list shows with nothing in the URL: every TLD. */
export const DEFAULT_TLD_SEARCH: TldSearch = { q: "", registry: "all", status: "all", priced: "all" };

/** A TLD is priced once a term of it is offered. */
const isPriced = (tld: TldPricing) => tld.costs.some((cost) => cost.enabled);

/** The TLDs that match the search (any part of the TLD, its dot optional) and the filters, in list order. */
export function arrangeTlds(tlds: readonly TldPricing[], { q, registry, status, priced }: TldSearch): TldPricing[] {
  const needle = q.trim().toLowerCase().replace(/^\./, "");
  return tlds.filter(
    (tld) =>
      tld.tld.includes(needle) &&
      (registry === "all" || tld.registry === registry) &&
      (status === "all" || tld.offered === (status === "offered")) &&
      (priced === "all" || isPriced(tld) === (priced === "priced")),
  );
}

const isFiltered = (search: TldSearch) =>
  search.q.trim() !== "" ||
  search.registry !== DEFAULT_TLD_SEARCH.registry ||
  search.status !== DEFAULT_TLD_SEARCH.status ||
  search.priced !== DEFAULT_TLD_SEARCH.priced;

type SearchProps = { search: TldSearch; onSearchChange: (next: Partial<TldSearch>) => void };

const STATUS_LABELS: Record<StatusFilter, () => string> = {
  all: m.tld_pricing_status_all,
  offered: m.tld_pricing_status_offered,
  withdrawn: m.tld_pricing_status_withdrawn,
};

const PRICED_LABELS: Record<PricedFilter, () => string> = {
  all: m.tld_pricing_priced_all,
  priced: m.tld_pricing_priced_yes,
  unpriced: m.tld_pricing_priced_no,
};

/** A filter's Select: its items drawn as options, the choice handed up. */
function Filter<T extends string>({
  label,
  items,
  value,
  onChange,
}: {
  label: string;
  items: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <Select items={items} value={value} onValueChange={(next: T | null) => next && onChange(next)}>
      <SelectTrigger aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {items.map((item) => (
          <SelectItem key={item.value} value={item.value}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Toolbar({ search, onSearchChange }: SearchProps) {
  const registries = [{ value: "all" as const, label: m.tld_pricing_registry_all() }, ...registryItems()];
  const statuses = STATUS_FILTERS.map((value) => ({ value, label: STATUS_LABELS[value]() }));
  const priced = PRICED_FILTERS.map((value) => ({ value, label: PRICED_LABELS[value]() }));
  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      {/* Uncontrolled: the URL is the state, and each keystroke is handed up to it. */}
      <Input
        type="search"
        className="sm:max-w-xs"
        defaultValue={search.q}
        placeholder={m.tld_pricing_search()}
        aria-label={m.tld_pricing_search()}
        onChange={(event) => onSearchChange({ q: event.target.value })}
      />
      <Filter
        label={m.tld_pricing_registry_filter()}
        items={registries}
        value={search.registry}
        onChange={(registry) => onSearchChange({ registry })}
      />
      <Filter
        label={m.tld_pricing_status_filter()}
        items={statuses}
        value={search.status}
        onChange={(status) => onSearchChange({ status })}
      />
      <Filter
        label={m.tld_pricing_priced_filter()}
        items={priced}
        value={search.priced}
        onChange={(next) => onSearchChange({ priced: next })}
      />
    </div>
  );
}

export function TldPricingTable({
  tlds,
  all,
  editable,
  search,
  onSearchChange,
  caption,
  pagination,
}: SearchProps & {
  /** The rows to draw: the page of the arranged list. */
  tlds: readonly TldPricing[];
  /** The whole list, in order: what "first" and "last" mean to the move buttons. */
  all: readonly TldPricing[];
  editable: boolean;
  caption?: string;
  pagination?: ReactNode;
}) {
  // Moving a TLD swaps it with its neighbour in the whole list, which a search or filter may hide.
  const filtered = isFiltered(search);
  const orderable = editable && !filtered;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{m.tld_pricing_list_title()}</CardTitle>
        {editable && filtered && <CardDescription>{m.tld_pricing_order_filtered()}</CardDescription>}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {all.length > 0 && <Toolbar search={search} onSearchChange={onSearchChange} />}
        {all.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>{m.tld_pricing_empty_title()}</EmptyTitle>
              <EmptyDescription>{m.tld_pricing_empty_description()}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : tlds.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>{m.tld_pricing_no_match_title()}</EmptyTitle>
              <EmptyDescription>{m.tld_pricing_no_match_body()}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <Table>
            {caption && <TableCaption className="text-left">{caption}</TableCaption>}
            <TableHeader>
              <TableRow>
                <TableHead>{m.tld_pricing_column_tld()}</TableHead>
                <TableHead />
                <TableHead>{m.tld_pricing_column_auto_registration()}</TableHead>
                <TableHead>{m.tld_pricing_column_offered()}</TableHead>
                {editable && <TableHead>{m.tld_pricing_column_actions()}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {tlds.map((tld) => (
                <TableRow key={tld.id}>
                  <TableCell>.{tld.tld}</TableCell>
                  <TableCell>
                    <SetTldCostDialog tld={tld} editable={editable} />
                  </TableCell>
                  <TableCell>
                    <TldRegistrySelect tld={tld} editable={editable} />
                  </TableCell>
                  <TableCell>
                    <TldOfferedSwitch tld={tld} editable={editable} />
                  </TableCell>
                  {editable && (
                    <TableCell>
                      {orderable && (
                        <div className="flex items-center gap-1">
                          <MoveTldButtons
                            tldId={tld.id}
                            tld={tld.tld}
                            first={all[0]?.id === tld.id}
                            last={all.at(-1)?.id === tld.id}
                          />
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {pagination}
      </CardContent>
    </Card>
  );
}
